import React, { useState } from 'react';
import { FaCheckCircle, FaUsers, FaClock, FaShieldAlt, FaChartLine, FaHandshake } from 'react-icons/fa';
import '../styles/ForEmployers.css';
import Toast from '../components/Toast';

const benefits = [
  {
    icon: <FaUsers />,
    title: 'Access to Top Talent',
    description: 'Gain access to our extensive network of pre-screened, qualified candidates ready to contribute from day one.'
  },
  {
    icon: <FaClock />,
    title: 'Faster Hiring',
    description: 'Our streamlined process significantly reduces time-to-hire, getting you the talent you need quickly.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Reduced Risk',
    description: 'Comprehensive background checks and verification processes ensure you hire with confidence.'
  },
  {
    icon: <FaChartLine />,
    title: 'Scalable Solutions',
    description: 'Whether you need one hire or a hundred, we scale our services to match your growth.'
  },
  {
    icon: <FaHandshake />,
    title: 'Dedicated Partnership',
    description: 'Your dedicated account manager ensures consistent communication and support throughout.'
  },
  {
    icon: <FaCheckCircle />,
    title: 'Quality Guarantee',
    description: 'Our satisfaction guarantee ensures you get the right fit for your organization.'
  }
];

const ForEmployers = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    positionType: '',
    message: ''
  });
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/employer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setToast({ isVisible: true, message: 'Thank you for your inquiry! Our team will contact you shortly.', type: 'success' });
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          industry: '',
          positionType: '',
          message: ''
        });
      } else {
        setToast({ isVisible: true, message: result.message || 'Failed to submit inquiry. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting employer form:', error);
      setToast({ isVisible: true, message: 'Failed to submit inquiry. Please try again later.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="employers-page">
      <Toast 
        message={toast.message} 
        type={toast.type} 
        isVisible={toast.isVisible} 
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
      <section className="page-hero">
        <div className="container">
          <h1>For Employers</h1>
          <p>Partner with us to find exceptional talent for your organization</p>
        </div>
      </section>

      <section className="employer-intro">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-content">
              <h2>Find the Right Talent, Fast</h2>
              <p>
                At JWCS, we understand that your employees are your greatest asset. That's why we've
                developed a comprehensive recruitment approach that goes beyond traditional staffing.
                We take the time to understand your organization's culture, goals, and specific needs
                to deliver candidates who don't just meet requirements—they exceed expectations.
              </p>
            </div>
            <div className="intro-image">
              <img
                src="/images/professionals.jpg"
                alt="Business professionals shaking hands"
                className="intro-img"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="destination-countries">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Global Reach</span>
            <h2 className="section-title">We Place Talent Worldwide</h2>
            <p className="section-description">Access skilled professionals for opportunities across these destination markets</p>
          </div>
          <div className="destinations-grid">
            <div className="destination-card">
              <img
                src="/images/middle-east-asia.png"
                alt="Middle East and Asia skyline"
              />
              <div className="destination-overlay">
                <h3>Middle East & Asia</h3>
                <p>Construction, Industrial, Logistics, Healthcare, Engineering, Oil & Gas etc.</p>
              </div>
            </div>
            <div className="destination-card">
              <img
                src="/images/central-europe.png"
                alt="Central Europe"
              />
              <div className="destination-overlay">
                <h3>Europe & Eastern Europe</h3>
                <p>Construction, Industrial, Healthcare, Logistics, Engineering etc.</p>
              </div>
            </div>
            <div className="destination-card">
              <img
                src="/images/uk-ireland.png"
                alt="United Kingdom and Ireland"
              />
              <div className="destination-overlay">
                <h3>United Kingdom & Ireland</h3>
                <p>Healthcare</p>
              </div>
            </div>
            <div className="destination-card">
              <img
                src="/images/ireland.png"
                alt="Canada"
              />
              <div className="destination-overlay">
                <h3>Canada & USA</h3>
                <p>Education, Immigration, Healthcare</p>
              </div>
            </div>
            <div className="destination-card">
              <img
                src="/images/australia.png"
                alt="Australia"
              />
              <div className="destination-overlay">
                <h3>Australia & New Zealand</h3>
                <p>Healthcare, Engineering and Immigration</p>
              </div>
            </div>
            <div className="destination-card">
              <img
                src="/images/africa.png"
                alt="Africa"
              />
              <div className="destination-overlay">
                <h3>Africa</h3>
                <p>Healthcare and Management</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="employer-benefits">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Why Partner With Us</span>
            <h2 className="section-title">Benefits for Employers</h2>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div className="benefit-card" key={index}>
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="employer-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Start Hiring Today</h2>
              <p>Tell us about your staffing needs and we'll connect you with qualified candidates</p>
            </div>

            <form className="employer-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contactName">Contact Name *</label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="industry">Industry *</label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Industry</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="technology">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="engineering">Engineering</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="positionType">Position Type *</label>
                  <select
                    id="positionType"
                    name="positionType"
                    value={formData.positionType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="permanent">Permanent</option>
                    <option value="contract">Contract</option>
                    <option value="temp-to-hire">Temp-to-Hire</option>
                    <option value="executive">Executive Search</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">Tell Us About Your Needs</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe the positions you need to fill, timeline, and any specific requirements..."
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForEmployers;
